import { formatTRY } from "@/lib/currency";
import { cn } from "@pos-vidyon/ui/lib/utils";
import { createFileRoute, useNavigate, Navigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Plus,
  Printer,
  Smartphone,
  User,
  UserPlus,
  Gift,
  Loader2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { orpc } from "@/utils/orpc";
import { toast } from "sonner";

import { useCart } from "@/components/dashboard/cart-context";
import { TAX_RATE, type OrderItem } from "@/components/dashboard/data";

import {
  PaymentPanel,
  type PaymentMethod,
  type Tender,
} from "@/components/dashboard/payment-panel";
import {
  PaymentCompleteDialog,
  type PaymentCompletion,
} from "@/components/dashboard/payment-complete-dialog";
import { SplitDialog, type PlannedShare } from "@/components/dashboard/split-dialog";
import { DashboardStatusBar } from "@/components/dashboard/status-bar";
import { DashboardTopBar } from "@/components/dashboard/top-bar";
import { useTerminalStore } from "@/store/terminal";
import * as m from "@/paraglide/messages.js";

export const Route = createFileRoute("/_auth/payment")({
  component: RouteComponent,
});

function RouteComponent() {
  const { session } = Route.useRouteContext();
  const navigate = useNavigate();
  const userName = session.data?.user.name ?? "Liam Basil";

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000 * 30);
    return () => clearInterval(interval);
  }, []);

  const cart = useCart();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("cash");
  // Boş = "kalanı tahsil et". Buraya bir varsayılan tutar koymak, kasiyerin fark
  // etmeden o tutarda kısmi ödeme almasına yol açar.
  const [cashReceived, setCashReceived] = useState("");
  // Units of each product moved into the "to pay" column. Empty = pay everything.
  const [selectedUnits, setSelectedUnits] = useState<Record<string, number>>({});
  // Equal split of the current payable amount; 1 = one tender for the whole bill.
  const [splitCount, setSplitCount] = useState(1);
  const [activeShareIndex, setActiveShareIndex] = useState(0);
  const [paidSharesList, setPaidSharesList] = useState<boolean[]>([]);
  // Running mixed-tender state for the amount currently due: how much has been
  // taken so far and via which methods. The bill closes only once it's cleared.
  const [paid, setPaid] = useState(0);
  const [tenders, setTenders] = useState<Tender[]>([]);

  const [plannedShares, setPlannedShares] = useState<PlannedShare[] | null>(null);
  const [isSplitDialogOpen, setIsSplitDialogOpen] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState<
    "connecting" | "processing" | "printing" | "success" | "error"
  >("connecting");
  const [errorMessage, setErrorMessage] = useState("");
  // Hata hem POS turundan hem yazdırmadan gelebiliyor; "Tekrar Dene" yanlış adımı
  // tekrarlarsa kart ikinci kez çekilir, o yüzden hangisi olduğunu saklıyoruz.
  const [errorRetry, setErrorRetry] = useState<"pos" | "print">("pos");
  // Ödeme alındı, fiş ekranı açık: kasiyer yazdır/kapat kararını verene kadar durur.
  const [completion, setCompletion] = useState<PaymentCompletion | null>(null);

  // Loyalty states
  const [loyaltyInput, setLoyaltyInput] = useState("");
  const [boundCard, setBoundCard] = useState<any | null>(null);
  const [useRewardActive, setUseRewardActive] = useState(false);
  const [isLoyaltyModalOpen, setIsLoyaltyModalOpen] = useState(false);

  const queryCardMutation = useMutation({
    ...orpc.loyalty.getCard.mutationOptions(),
    onSuccess: (data) => {
      setBoundCard(data);
      setLoyaltyInput("");
      setIsLoyaltyModalOpen(false);
      toast.success(m.payment_customerConnected({ name: data.customer.name }));
    },
    onError: (err) => {
      toast.error(err.message || m.payment_cardNotFound());
    },
  });

  const useRewardMutation = useMutation({
    ...orpc.loyalty.useReward.mutationOptions(),
    onSuccess: () => {
      toast.success(m.payment_rewardUsedSuccess());
    },
    onError: (err) => {
      console.error("Failed to use reward:", err);
      toast.error(m.payment_loyaltyServerError());
    },
  });

  const addStampMutation = useMutation({
    ...orpc.loyalty.addStamp.mutationOptions(),
    onSuccess: () => {
      toast.success(m.payment_stampAddedSuccess());
    },
    onError: (err) => {
      console.error("Failed to add stamp:", err);
      toast.error(m.payment_loyaltyServerError());
    },
  });

  function handleQueryCard() {
    if (!loyaltyInput.trim()) return;
    queryCardMutation.mutate({ codeOrPhone: loyaltyInput.trim() });
  }

  function handleNumpadPress(digit: string) {
    if (loyaltyInput.length < 8) {
      setLoyaltyInput((prev) => prev + digit);
    }
  }

  function handleNumpadBackspace() {
    setLoyaltyInput((prev) => prev.slice(0, -1));
  }

  function handleDisconnectCard() {
    setBoundCard(null);
    setUseRewardActive(false);
    toast.info(m.payment_customerDisconnected());
  }

  const { printerType, printerIp, printerPort, posType, posIp, posPort, posSerial } =
    useTerminalStore();

  if (!cart.selectedTableId) {
    return <Navigate to="/tables" replace />;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (cart.orderItems.length === 0) {
      navigate({ to: "/dashboard" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const orderItems = cart.orderItems;

  // Items chosen for a partial payment, carrying only the selected quantity.
  const selectedItems = useMemo<OrderItem[]>(
    () =>
      orderItems
        .map((item) => ({ ...item, quantity: selectedUnits[item.productId] ?? 0 }))
        .filter((item) => item.quantity > 0),
    [orderItems, selectedUnits],
  );

  const hasSelection = selectedItems.length > 0;
  const totalUnits = orderItems.reduce((sum, item) => sum + item.quantity, 0);
  const selectedUnitCount = selectedItems.reduce((sum, item) => sum + item.quantity, 0);
  // Selecting every unit is the same as paying the whole bill.
  const payingEverything = !hasSelection || selectedUnitCount === totalUnits;

  const payableSubtotal = payingEverything
    ? cart.subtotal
    : selectedItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const payableTax = payableSubtotal * TAX_RATE;
  const payableTotal = payableSubtotal + payableTax;

  // Split only applies when settling the full bill; a partial selection is its
  // own share, so the stepper is disabled while items are picked.
  const effectiveSplit = payingEverything ? splitCount : 1;
  const totalSharesCount = plannedShares ? plannedShares.length : effectiveSplit;

  // Sync paidSharesList and activeShareIndex whenever totalSharesCount changes
  useEffect(() => {
    setPaidSharesList(Array.from({ length: totalSharesCount }, () => false));
    setActiveShareIndex(0);
  }, [totalSharesCount]);

  const amountDue = plannedShares
    ? (plannedShares[activeShareIndex]?.amount ?? 0)
    : payableTotal / totalSharesCount;
  const remaining = Math.max(0, amountDue - paid);

  // Any change to what's being paid (items or split) invalidates the tenders
  // already taken toward the previous amount, so wipe the running balance.
  function resetTenders() {
    setPaid(0);
    setTenders([]);
    setActiveShareIndex(0);
    setPaidSharesList(Array.from({ length: totalSharesCount }, () => false));
  }

  function addUnit(item: OrderItem) {
    const current = selectedUnits[item.productId] ?? 0;
    if (current >= item.quantity) return;
    // A fresh selection resets any pending split.
    if (splitCount !== 1) setSplitCount(1);
    setPlannedShares(null);
    resetTenders();
    setSelectedUnits((prev) => ({ ...prev, [item.productId]: current + 1 }));
  }

  function removeUnit(productId: string) {
    resetTenders();
    setPlannedShares(null);
    setSelectedUnits((prev) => {
      const current = prev[productId] ?? 0;
      if (current <= 1) {
        const next = { ...prev };
        delete next[productId];
        return next;
      }
      return { ...prev, [productId]: current - 1 };
    });
  }

  function changeSplit(count: number) {
    resetTenders();
    setPlannedShares(null);
    setSplitCount(count);
  }

  function clearSelection() {
    resetTenders();
    setPlannedShares(null);
    setSelectedUnits({});
  }

  function handleKeypadPress(key: string) {
    setCashReceived((current) => {
      if (key === "C") return "";
      if (key === "back") return current.slice(0, -1);
      if (key === "." && current.includes(".")) return current;
      return current + key;
    });
  }

  // Adisyonu kapatıp masaya döner. Id'ler çağıranın doğruladığı değerlerden gelir:
  // burada cart'ı yeniden okumak, adisyon bir şekilde null göründüğünde kapatmayı
  // sessizce atlayıp masayı açık bırakma riski taşıyordu.
  function settleAndLeave(tableId: string, adisyonId: string) {
    cart.closeAdisyon(tableId, adisyonId);
    navigate({ to: "/tables" });
  }

  // Bakiye kapandığı andaki işlemi dondurur: kasiyer fişi yazdırmaya karar
  // verene kadar tutarlar değişmesin. Tender listesi parametreyle gelir, çünkü
  // setTenders henüz uygulanmamış olabilir.
  function buildCompletion(finalTenders: Tender[], changeDue: number): PaymentCompletion {
    const paidShareCount = paidSharesList.filter(Boolean).length;
    const statusNote = !payingEverything
      ? "Seçili ürünler ödendi, hesabın kalanı açık kalacak."
      : totalSharesCount > 1
        ? paidShareCount + 1 < totalSharesCount
          ? `${activeShareIndex + 1}. pay ödendi, sıradaki paya geçilecek.`
          : "Son pay ödendi, masa kapatılacak."
        : "Masa kapatılacak.";

    return {
      tableName: cart.selectedTable?.name || m.payment_unknownTable(),
      receiptItems: payingEverything ? orderItems : selectedItems,
      subtotal: payableSubtotal,
      tax: payableTax,
      total: payableTotal,
      tenders: finalTenders,
      change: changeDue,
      cashDrawerOpened: finalTenders.some((tender) => tender.method === "cash"),
      statusNote,
    };
  }

  // Kart dışındaki yöntemlerde çekilecek bir POS turu yok; fiş ekranı doğrudan açılır.
  async function startPaymentFlow() {
    if (paymentMethod !== "card") return;

    setErrorMessage("");
    setErrorRetry("pos");
    setIsProcessing(true);
    setProcessingStep("connecting");
    try {
      if (window.desktopRpc) {
        const posRes = await window.desktopRpc.request.sendPosPayment({
          posType,
          posIp,
          posPort: Number(posPort),
          posSerial,
          amount: payableTotal,
        });

        if (!posRes.success) {
          throw new Error(posRes.error || m.payment_posPaymentFailed());
        }
      } else {
        // Simulator fallback
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      // POS onayladı; fiş ekranı devralsın.
      setIsProcessing(false);
    } catch (err: any) {
      setProcessingStep("error");
      setErrorMessage(err.message || String(err));
    }
  }

  // Fiş ekranındaki "Fiş Yazdır".
  async function handlePrintReceipt() {
    setErrorMessage("");
    setErrorRetry("print");
    setIsProcessing(true);
    setProcessingStep("printing");
    try {
      await printReceipt();
      setIsProcessing(false);
      setCompletion(null);
      void finalizeSafely();
    } catch (err: any) {
      setProcessingStep("error");
      setErrorMessage(err.message || String(err));
    }
  }

  // Fiş ekranındaki "Kapat" — fişsiz kapatır, ödeme yine de işlenmiştir.
  // finalizePayment async: catch'siz bırakılırsa içindeki bir hata sessizce
  // yutulur ve "panel kapanır ama adisyon kapanmaz" görünür.
  function handleCloseReceipt() {
    setIsProcessing(false);
    setCompletion(null);
    void finalizeSafely();
  }

  async function finalizeSafely() {
    try {
      await finalizePayment();
    } catch (err: any) {
      console.error("finalizePayment failed:", err);
      toast.error(`Adisyon kapatılamadı: ${err?.message ?? String(err)}`);
    }
  }

  async function printReceipt() {
    const receiptItems = payingEverything ? orderItems : selectedItems;
    const receiptData = {
      tableName: cart.selectedTable?.name || m.payment_unknownTable(),
      date: new Date().toLocaleString("tr-TR"),
      items: receiptItems.map((item) => ({
        name: item.name,
        quantity: item.quantity,
        price: item.price,
      })),
      subtotal: payableSubtotal,
      tax: payableTax,
      total: payableTotal,
    };

    if (window.desktopRpc) {
      const res = await window.desktopRpc.request.printReceipt({
        printerType,
        printerIp,
        printerPort: Number(printerPort),
        receipt: receiptData,
      });
      if (!res.success) {
        throw new Error(res.error || m.payment_receiptPrintFailed());
      }
    } else {
      // Simulator fallback
      await new Promise((resolve) => setTimeout(resolve, 1000));
      console.log("Mock print:", receiptData);
    }
  }

  async function finalizePayment() {
    const tableId = cart.selectedTableId;
    const adisyonId = cart.selectedAdisyon?.id;

    if (boundCard) {
      try {
        if (useRewardActive) {
          await useRewardMutation.mutateAsync({ cardId: boundCard.card.id });
        } else {
          await addStampMutation.mutateAsync({ cardId: boundCard.card.id, stampsToAdd: 1 });
        }
      } catch {
        // Hata mutation onError tarafından toast olarak gösterildi.
      }
    }

    if (!tableId || !adisyonId) {
      setIsProcessing(false);
      navigate({ to: "/tables" });
      return;
    }

    if (!payingEverything) {
      // Partial item selection: those items are paid; the rest stays open.
      cart.settleUnits(tableId, adisyonId, selectedUnits);
      setSelectedUnits({});
      resetTenders();
      setCashReceived("");
      setIsProcessing(false);
      return;
    }

    if (totalSharesCount > 1) {
      const nextPaidList = [...paidSharesList];
      nextPaidList[activeShareIndex] = true;
      setPaidSharesList(nextPaidList);

      const allPaid = nextPaidList.every((status) => status === true);
      if (!allPaid) {
        const firstUnpaid = nextPaidList.findIndex((status) => !status);
        setActiveShareIndex(firstUnpaid);
        setPaid(0);
        setTenders([]);
        setCashReceived("");
        setIsProcessing(false);
        return;
      }
    }

    // Whole bill (or last share): close the adisyon and free the table.
    setIsProcessing(false);
    settleAndLeave(tableId, adisyonId);
  }

  function handleCompletePayment() {
    const tableId = cart.selectedTableId;
    const adisyonId = cart.selectedAdisyon?.id;
    if (!tableId || !adisyonId) return;

    // An empty keypad means "charge whatever is left" (handy for the closing
    // card tender); a number caps the tender at the outstanding balance.
    const entered = Number.parseFloat(cashReceived || "0");
    const tenderAmount = entered > 0 ? entered : remaining;
    const applied = Math.min(tenderAmount, remaining);
    if (applied <= 0) return;

    const newPaid = paid + applied;
    const cleared = newPaid >= amountDue - 0.005;
    const finalTenders = [...tenders, { method: paymentMethod, amount: applied }];

    // Not cleared yet: record this tender and keep the bill open for the rest
    // (e.g. 200 cash now, 50 on card next).
    if (!cleared) {
      setPaid(newPaid);
      setTenders(finalTenders);
      setCashReceived("");
      return;
    }

    // Balance cleared — freeze the receipt, then run POS if the tender is a card.
    const changeDue = paymentMethod === "cash" ? Math.max(0, entered - remaining) : 0;
    setTenders(finalTenders);
    setCashReceived("");
    setCompletion(buildCompletion(finalTenders, changeDue));
    startPaymentFlow();
  }

  const dateLabel = now.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const timeLabel = now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="flex h-screen max-h-screen flex-col overflow-hidden bg-[#f7f7f8]">
      <DashboardTopBar
        tableNumber={cart.selectedTable?.name ?? ""}
        notificationCount={3}
        dateLabel={dateLabel}
        timeLabel={timeLabel}
        userName={userName}
        userRole={session.data?.user.role || "Manager"}
        userImage={session.data?.user.image}
      />

      <div className="flex flex-col flex-1 overflow-hidden px-6 py-5">
        <div className="mb-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate({ to: "/dashboard" })}
              className="flex size-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-gray-800 shrink-0"
            >
              <ArrowLeft className="size-4" />
            </button>
            <div className="leading-tight">
              <h1 className="text-2xl font-bold text-gray-900">{m.payment_paymentTitle()}</h1>
              <p className="text-xs text-gray-400 mt-0.5">
                {m.payment_completePaymentFor({
                  table: cart.selectedTable?.name || m.payment_unknownTable(),
                })}
              </p>
            </div>
          </div>

          {/* Header Action Buttons Group */}
          <div className="flex items-center gap-3 shrink-0">
            {/* Loyalty pass connection button / connected profile badge */}
            {!boundCard ? (
              <button
                type="button"
                onClick={() => {
                  setLoyaltyInput("");
                  setIsLoyaltyModalOpen(true);
                }}
                className="flex h-12 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 active:bg-gray-100 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
              >
                <UserPlus className="size-4 text-gray-400" />
                <span>Sadakat Kartı</span>
              </button>
            ) : (
              <div className="flex h-12 items-center gap-3 rounded-xl border border-gray-200 bg-white pl-3 pr-2">
                <div className="flex items-center gap-2.5">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500">
                    <User className="size-4" />
                  </div>
                  <div className="text-left leading-tight">
                    <span className="block text-sm font-semibold text-gray-900">
                      {boundCard.customer.name}
                    </span>
                    <span className="block text-xs text-gray-400 tabular-nums">
                      {m.payment_currentStamp({
                        current: boundCard.card.stampsCount,
                        target: boundCard.settings.targetStamps,
                      })}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 border-l border-gray-100 pl-2.5">
                  {boundCard.card.rewardCount > 0 ? (
                    <button
                      type="button"
                      onClick={() => setUseRewardActive(!useRewardActive)}
                      aria-pressed={useRewardActive}
                      className={cn(
                        "flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors cursor-pointer",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-1",
                        useRewardActive
                          ? "border-gray-900 bg-gray-900 text-white"
                          : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50 active:bg-gray-100",
                      )}
                    >
                      <Gift
                        className={cn("size-3.5", useRewardActive ? "text-white" : "text-gray-400")}
                      />
                      <span>
                        {useRewardActive
                          ? m.payment_rewardActive()
                          : m.payment_useReward({ count: boundCard.card.rewardCount })}
                      </span>
                    </button>
                  ) : (
                    <span className="rounded-lg bg-gray-50 px-2.5 py-1.5 text-xs font-medium text-gray-400">
                      {m.payment_noReward()}
                    </span>
                  )}

                  <button
                    type="button"
                    onClick={handleDisconnectCard}
                    aria-label={m.payment_disconnectLoyalty()}
                    title={m.payment_disconnectLoyalty()}
                    className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-1"
                  >
                    <X className="size-4" />
                  </button>
                </div>
              </div>
            )}

            {/* General Total Bill Amount Box */}
            <div className="flex select-none flex-col items-end justify-center border-l border-gray-200 pl-3 leading-none">
              <span className="text-xs font-medium text-gray-400">Toplam Tutar</span>
              <span className="mt-1 text-2xl font-bold tracking-tight text-gray-950 tabular-nums">
                {formatTRY(cart.total)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <AllItemsColumn
            items={orderItems}
            selectedUnits={selectedUnits}
            onAddUnit={addUnit}
            subtotal={cart.subtotal}
            tax={cart.tax}
            total={cart.total}
          />

          <ToPayColumn
            items={selectedItems}
            onRemoveUnit={removeUnit}
            onClear={clearSelection}
            subtotal={payableSubtotal}
            tax={payableTax}
            total={payableTotal}
            payingEverything={payingEverything}
          />

          {/* Column 3: Payment Panel */}
          <PaymentPanel
            amountDue={amountDue}
            remaining={remaining}
            paid={paid}
            tenders={tenders}
            paymentMethod={paymentMethod}
            onSelectMethod={setPaymentMethod}
            cashReceived={cashReceived}
            onKeypadPress={handleKeypadPress}
            onSetAmount={setCashReceived}
            onCompletePayment={handleCompletePayment}
            splitCount={splitCount}
            onChangeSplit={changeSplit}
            splitDisabled={hasSelection && !payingEverything}
            completeLabel={
              paid > 0.005 && remaining > 0.005
                ? `${m.payment_addPayment()} · ${formatTRY(remaining)} ${m.payment_left()}`
                : !payingEverything
                  ? m.payment_paySelected()
                  : totalSharesCount > 1
                    ? `${m.payment_payShare()} ${activeShareIndex + 1}/${totalSharesCount}`
                    : m.payment_completePayment()
            }
            plannedShares={plannedShares}
            onOpenSplit={() => setIsSplitDialogOpen(true)}
            activeShareIndex={activeShareIndex}
            paidSharesList={paidSharesList}
          />
        </div>
      </div>

      <DashboardStatusBar />

      {completion && !isProcessing && (
        <PaymentCompleteDialog
          completion={completion}
          dateLabel={dateLabel}
          timeLabel={timeLabel}
          onPrint={handlePrintReceipt}
          onClose={handleCloseReceipt}
        />
      )}

      {isProcessing && (
        <div className="fixed inset-0 z-[160] flex items-center justify-center bg-black/75 backdrop-blur-md transition-all duration-300">
          <div className="w-[450px] max-w-full rounded-2xl border border-gray-800 bg-gray-900/90 text-white p-6 shadow-2xl flex flex-col items-center text-center">
            {processingStep === "connecting" && (
              <div className="space-y-6 py-4 w-full">
                <div className="relative flex items-center justify-center mx-auto">
                  <div className="absolute size-16 rounded-full border-4 border-emerald-500/20 animate-ping" />
                  <div className="relative size-16 rounded-full border-4 border-t-emerald-500 border-r-transparent border-b-transparent border-l-transparent animate-spin flex items-center justify-center text-emerald-400 font-bold text-lg">
                    <Smartphone className="size-8 animate-pulse" />
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-bold">{m.payment_posConnecting()}</h3>
                  <p className="text-sm text-gray-400">{m.payment_posConnectingDesc()}</p>
                  <div className="mt-4 rounded-xl bg-gray-800/50 p-3.5 text-xs text-left text-gray-300 space-y-1.5 border border-gray-700/50">
                    <p>
                      <span className="font-semibold text-gray-400">{m.payment_serialNo()}:</span>{" "}
                      {posSerial}
                    </p>
                    {posType === "network" && (
                      <p>
                        <span className="font-semibold text-gray-400">{m.payment_deviceIp()}:</span>{" "}
                        {posIp}:{posPort}
                      </p>
                    )}
                    <p>
                      <span className="font-semibold text-gray-400">
                        {m.payment_transactionAmount()}:
                      </span>{" "}
                      {cart.total.toFixed(2)} TL
                    </p>
                  </div>
                </div>
              </div>
            )}

            {processingStep === "printing" && (
              <div className="space-y-6 py-4">
                <div className="relative flex items-center justify-center mx-auto">
                  <div className="absolute size-16 rounded-full border-4 border-blue-500/20 animate-ping" />
                  <div className="relative size-16 rounded-full border-4 border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent animate-spin flex items-center justify-center text-blue-400 font-bold text-lg">
                    <Printer className="size-8 animate-pulse" />
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-bold">{m.payment_receiptPrinting()}</h3>
                  <p className="text-sm text-gray-400">{m.payment_receiptPrintingDesc()}</p>
                </div>
              </div>
            )}

            {processingStep === "success" && (
              <div className="space-y-6 py-4">
                <div className="size-16 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto">
                  <svg
                    className="size-10 animate-bounce"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-bold text-emerald-400 font-semibold text-xl">
                    {m.payment_transactionComplete()}
                  </h3>
                  <p className="text-sm text-gray-400">{m.payment_transactionCompleteDesc()}</p>
                </div>
              </div>
            )}

            {processingStep === "error" && (
              <div className="space-y-6 py-4 w-full">
                <div className="size-16 rounded-full bg-rose-500/20 text-rose-400 flex items-center justify-center mx-auto">
                  <svg className="size-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-bold text-rose-400">{m.payment_connectionError()}</h3>
                  <p className="text-xs text-rose-200/80 bg-rose-950/40 p-3 rounded-lg border border-rose-900/50 break-words  text-left max-h-24 overflow-y-auto">
                    {errorMessage}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <button
                    onClick={() =>
                      errorRetry === "print" ? handlePrintReceipt() : startPaymentFlow()
                    }
                    className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white px-4 py-2.5 text-xs font-bold transition-all cursor-pointer"
                  >
                    {m.payment_retry()}
                  </button>
                  <button
                    onClick={handleCloseReceipt}
                    className="w-full rounded-xl bg-amber-600 hover:bg-amber-700 active:bg-amber-800 text-white px-4 py-2.5 text-xs font-bold transition-all cursor-pointer"
                  >
                    {m.payment_cashCloseManual()}
                  </button>
                </div>
                <button
                  onClick={() => setIsProcessing(false)}
                  className="mt-3 text-xs text-gray-500 hover:text-gray-300 font-medium underline cursor-pointer"
                >
                  {m.payment_giveUp()}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Advanced Split Dialog */}
      {isSplitDialogOpen && (
        <SplitDialog
          items={orderItems}
          total={cart.total}
          onApply={(shares) => {
            setPlannedShares(shares);
            setSplitCount(shares.length);
            resetTenders();
            setIsSplitDialogOpen(false);
          }}
          onClose={() => setIsSplitDialogOpen(false)}
        />
      )}

      {/* Loyalty Modal */}
      {isLoyaltyModalOpen && (
        <div className="fixed inset-0 z-[160] flex items-end justify-center bg-black/60 backdrop-blur-sm transition-all duration-300">
          <div className="w-full max-w-[400px] rounded-t-[32px] bg-white p-6 pb-8 shadow-2xl flex flex-col relative animate-in slide-in-from-bottom duration-300 ease-out">
            {/* Pull Handle */}
            <div className="mx-auto w-12 h-1 bg-gray-200 rounded-full mb-2 shrink-0" />

            <button
              onClick={() => setIsLoyaltyModalOpen(false)}
              className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 p-1.5 hover:bg-gray-100 rounded-xl transition-colors cursor-pointer"
            >
              <X className="size-5" />
            </button>

            {/* Virtual Loyalty Card wrapper */}
            <div className="relative w-full aspect-[1.6/1] rounded-2xl bg-gradient-to-br from-zinc-600 via-zinc-700 to-slate-800 p-5 shadow-xl border border-white/10 overflow-hidden flex flex-col justify-between mt-6 mb-6 text-white select-none shrink-0">
              {/* Card background glowing accents */}
              <div className="absolute -right-10 -top-10 size-40 rounded-full bg-white/5 blur-3xl pointer-events-none" />
              <div className="absolute -left-10 -bottom-10 size-40 rounded-full bg-zinc-500/10 blur-3xl pointer-events-none" />

              {/* Card Header Row */}
              <div className="flex items-center justify-between">
                {/* Chip icon */}
                <div className="w-8 h-6 bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-500 rounded-md border border-amber-200/50 shadow-sm relative overflow-hidden flex flex-col p-1 justify-between">
                  <div className="flex justify-between h-[2px]">
                    <div className="w-[2px] bg-amber-700/30" />
                    <div className="w-[2px] bg-amber-700/30" />
                    <div className="w-[2px] bg-amber-700/30" />
                  </div>
                  <div className="w-full h-[1px] bg-amber-700/20" />
                  <div className="flex justify-between h-[2px]">
                    <div className="w-[2px] bg-amber-700/30" />
                    <div className="w-[2px] bg-amber-700/30" />
                    <div className="w-[2px] bg-amber-700/30" />
                  </div>
                </div>

                {/* Card Title Label */}
                <span className="text-[10px] tracking-wider text-zinc-200 font-bold uppercase opacity-85">
                  {m.payment_loyaltyCard()}
                </span>
              </div>

              {/* Digit Slots inside Card */}
              <div className="flex items-center justify-between gap-1 my-3">
                {/* First 4 digits */}
                <div className="flex flex-1 gap-1">
                  {Array.from({ length: 4 }).map((_, i) => {
                    const digit = loyaltyInput[i];
                    const isActive = loyaltyInput.length === i;
                    return (
                      <div
                        key={i}
                        className={cn(
                          "flex-1 aspect-square rounded-lg border flex items-center justify-center text-xl font-bold transition-all duration-150",
                          digit
                            ? "border-white/30 bg-white/20 text-white shadow-inner"
                            : isActive
                              ? "border-white/80 bg-white/10 ring-4 ring-white/10 animate-pulse text-white"
                              : "border-white/10 bg-white/5 text-white/40",
                        )}
                      >
                        {digit || ""}
                      </div>
                    );
                  })}
                </div>

                {/* Separator dash */}
                <div className="w-2 h-[2px] bg-white/30 rounded shrink-0 mx-0.5" />

                {/* Last 4 digits */}
                <div className="flex flex-1 gap-1">
                  {Array.from({ length: 4 }).map((_, idx) => {
                    const i = idx + 4;
                    const digit = loyaltyInput[i];
                    const isActive = loyaltyInput.length === i;
                    return (
                      <div
                        key={i}
                        className={cn(
                          "flex-1 aspect-square rounded-lg border flex items-center justify-center text-xl font-bold transition-all duration-150",
                          digit
                            ? "border-white/30 bg-white/20 text-white shadow-inner"
                            : isActive
                              ? "border-white/80 bg-white/10 ring-4 ring-white/10 animate-pulse text-white"
                              : "border-white/10 bg-white/5 text-white/40",
                        )}
                      >
                        {digit || ""}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Card Footer Row */}
              <div className="flex items-end justify-between leading-none">
                <div>
                  <span className="block text-[8px] text-zinc-300 uppercase tracking-widest font-semibold opacity-75">
                    Loyalty Member
                  </span>
                  <span className="block text-xs  mt-0.5 text-white/90">VIDYON PASS</span>
                </div>

                {/* Barcode representation */}
                <div className="flex items-center gap-[2px] opacity-45">
                  <div className="w-[1px] h-5 bg-white" />
                  <div className="w-[2px] h-5 bg-white" />
                  <div className="w-[1px] h-5 bg-white" />
                  <div className="w-[3px] h-5 bg-white" />
                  <div className="w-[1px] h-5 bg-white" />
                  <div className="w-[2px] h-5 bg-white" />
                  <div className="w-[1px] h-5 bg-white" />
                  <div className="w-[1px] h-5 bg-white" />
                  <div className="w-[4px] h-5 bg-white" />
                </div>
              </div>
            </div>

            {/* Keypad */}
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => handleNumpadPress(num.toString())}
                  className="h-14 rounded-2xl bg-gray-50 hover:bg-gray-100 active:bg-gray-200 text-gray-800 font-bold text-lg transition-all active:scale-95 flex items-center justify-center cursor-pointer shadow-sm border border-gray-100"
                >
                  {num}
                </button>
              ))}

              <button
                type="button"
                onClick={() => setLoyaltyInput("")}
                className="h-14 rounded-2xl bg-rose-50 hover:bg-rose-100 active:bg-rose-200 text-rose-600 font-bold text-base transition-all active:scale-95 flex items-center justify-center cursor-pointer shadow-sm border border-rose-100"
              >
                C
              </button>
              <button
                type="button"
                onClick={() => handleNumpadPress("0")}
                className="h-14 rounded-2xl bg-gray-50 hover:bg-gray-100 active:bg-gray-200 text-gray-800 font-bold text-lg transition-all active:scale-95 flex items-center justify-center cursor-pointer shadow-sm border border-gray-100"
              >
                0
              </button>
              <button
                type="button"
                onClick={handleNumpadBackspace}
                className="h-14 rounded-2xl bg-gray-50 hover:bg-gray-100 active:bg-gray-200 text-gray-600 font-bold text-lg transition-all active:scale-95 flex items-center justify-center cursor-pointer shadow-sm border border-gray-100"
              >
                <span className="text-xl">⌫</span>
              </button>
            </div>

            {/* Action button */}
            <button
              type="button"
              onClick={handleQueryCard}
              disabled={queryCardMutation.isPending || loyaltyInput.length !== 8}
              className="w-full mt-5 h-12 rounded-2xl bg-gray-900 hover:bg-gray-800 active:bg-black text-white font-bold text-sm flex items-center justify-center gap-2 transition-all active:scale-95 shadow-md disabled:opacity-40 disabled:hover:bg-gray-900 cursor-pointer"
            >
              {queryCardMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <>{m.payment_findCard()}</>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface AllItemsColumnProps {
  items: OrderItem[];
  selectedUnits: Record<string, number>;
  onAddUnit: (item: OrderItem) => void;
  subtotal: number;
  tax: number;
  total: number;
}

// Left column — the full adisyon. Tap a line to move a unit into "To Pay".
function AllItemsColumn({
  items,
  selectedUnits,
  onAddUnit,
  subtotal,
  tax,
  total,
}: AllItemsColumnProps) {
  return (
    <div className="flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-4">
      <div className="mb-3">
        <h2 className="text-base font-semibold text-gray-900">Full Order</h2>
        <p className="text-xs text-gray-400">Tap an item to add it to the payment.</p>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto">
        {items.map((item) => {
          const picked = selectedUnits[item.productId] ?? 0;
          const remaining = item.quantity - picked;
          const disabled = remaining <= 0;
          return (
            <button
              key={item.productId}
              onClick={() => onAddUnit(item)}
              disabled={disabled}
              className={cn(
                "flex w-full items-center justify-between rounded-xl border p-2.5 text-left transition-colors",
                disabled
                  ? "border-gray-100 bg-gray-50 opacity-60"
                  : "border-gray-100 hover:border-emerald-300 hover:bg-emerald-50/50",
              )}
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-gray-900">
                  {remaining} × {item.name}
                </p>
                <p className="text-xs text-gray-400">
                  {formatTRY(item.price)}
                  {picked > 0 && <span className="ml-1 text-emerald-600">· {picked} seçili</span>}
                </p>
              </div>
              <span
                className={cn(
                  "flex size-6 shrink-0 items-center justify-center rounded-full",
                  disabled ? "bg-gray-200 text-gray-400" : "bg-emerald-100 text-emerald-700",
                )}
              >
                <Plus className="size-3.5" />
              </span>
            </button>
          );
        })}
      </div>

      <SummaryFooter subtotal={subtotal} tax={tax} total={total} totalLabel="Order Total" />
    </div>
  );
}

interface ToPayColumnProps {
  items: OrderItem[];
  onRemoveUnit: (productId: string) => void;
  onClear: () => void;
  subtotal: number;
  tax: number;
  total: number;
  payingEverything: boolean;
}

// Middle column — the items currently queued for this tender.
function ToPayColumn({
  items,
  onRemoveUnit,
  onClear,
  subtotal,
  tax,
  total,
  payingEverything,
}: ToPayColumnProps) {
  const empty = items.length === 0;
  return (
    <div className="flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">To Pay</h2>
          <p className="text-xs text-gray-400">
            {empty ? "Whole bill will be charged." : "Selected items for this payment."}
          </p>
        </div>
        {!empty && (
          <button
            onClick={onClear}
            className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            Clear
          </button>
        )}
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto">
        {empty ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-gray-100 text-gray-400">
              <Plus className="size-5" />
            </div>
            <p className="max-w-[16rem] text-sm text-gray-400">
              Soldan ürün seçin veya tüm hesabı ödemek için doğrudan devam edin.
            </p>
          </div>
        ) : (
          items.map((item) => (
            <div
              key={item.productId}
              className="flex items-center justify-between rounded-xl border border-emerald-100 bg-emerald-50/40 p-2.5"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-gray-900">
                  {item.quantity} × {item.name}
                </p>
                <p className="text-xs text-gray-400">{formatTRY(item.price * item.quantity)}</p>
              </div>
              <button
                onClick={() => onRemoveUnit(item.productId)}
                className="flex size-6 shrink-0 items-center justify-center rounded-full bg-white text-gray-400 hover:text-red-500"
              >
                <X className="size-3.5" />
              </button>
            </div>
          ))
        )}
      </div>

      <SummaryFooter
        subtotal={subtotal}
        tax={tax}
        total={total}
        totalLabel={payingEverything ? "Full Total" : "Selected Total"}
      />
    </div>
  );
}

interface SummaryFooterProps {
  subtotal: number;
  tax: number;
  total: number;
  totalLabel: string;
}

function SummaryFooter({ subtotal, tax, total, totalLabel }: SummaryFooterProps) {
  return (
    <>
      <div className="mt-4 space-y-1.5 border-t border-gray-100 pt-3 text-sm">
        <div className="flex items-center justify-between text-gray-500">
          <span>Subtotal</span>
          <span>{formatTRY(subtotal)}</span>
        </div>
        <div className="flex items-center justify-between text-gray-500">
          <span>Tax ({Math.round(TAX_RATE * 100)}%)</span>
          <span>{formatTRY(tax)}</span>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between border-t border-gray-100 pt-3">
        <span className="text-base font-semibold text-gray-900">{totalLabel}</span>
        <span className="text-lg font-bold text-gray-900">{formatTRY(total)}</span>
      </div>
    </>
  );
}
